import { useState } from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import {
  getValidationSchema,
  initialValues as initialValuesBase,
} from "./config";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { StyledFormicError } from "./styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { Tag } from "types/tag";
import {
  useAddTagMutation,
  useUpdateTagMutation,
} from "graphql/__generated__/hooks";
import { Modal } from "components/Modal";
import { Messages, NotificationSeverity, TAG_TYPES } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";

export type Props = {
  createOrEditTagModal: boolean;
  handleCreateOrEditTagModalDismiss: () => void;
  handleCreateOrEditTagChange: (tag?: Tag) => string | void;
  tag?: Tag | null;
};

const CreateOrEditTagModal = ({
  createOrEditTagModal,
  handleCreateOrEditTagModalDismiss,
  handleCreateOrEditTagChange,
  tag = null,
}: Props) => {
  const isEditMode = tag !== null;
  const [loading, setLoading] = useState(false);
  const [addTag] = useAddTagMutation();
  const [updateTag] = useUpdateTagMutation();
  const { notify } = useNotifications();

  const handleCreateOrEditTag = async (reqValues: Tag) => {
    setLoading(true);

    await addTag({
      variables: reqValues,
    });
    handleCreateOrEditTagChange();
    setLoading(false);
    notify({
      message: Messages.successTagCreated,
      severity: NotificationSeverity.success,
    });
  };

  const handleEditTag = async (reqValues: Tag) => {
    setLoading(true);

    await updateTag({
      variables: {
        id: tag?.tag_id || "",
        tag: reqValues,
      },
    });
    handleCreateOrEditTagChange();
    setLoading(false);
    notify({
      message: Messages.successTagUpdated,
      severity: NotificationSeverity.success,
    });
  };

  const initialValues = isEditMode
    ? {
        tagType: tag?.type || "",
        tagName: tag?.name || "",
      }
    : initialValuesBase;

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        type: values.tagType,
        name: values.tagName,
      };
      if (isEditMode) {
        handleEditTag(reqValues as Tag).then();
      } else {
        handleCreateOrEditTag(reqValues as Tag).then();
      }
    },
  });

  return (
    <Modal
      open={createOrEditTagModal}
      onClose={handleCreateOrEditTagModalDismiss}
      title={isEditMode ? tag?.name : "Create a new Tag"}
      size="small"
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mt: 1 }}>
            <Box>
              <FormControl variant="standard" fullWidth margin="dense">
                <InputLabel id="tagTypeLabel">Tag Type</InputLabel>
                <Select
                  labelId="tagTypeLabel"
                  id="tagType"
                  name="tagType"
                  value={formik?.values?.tagType}
                  onChange={formik.handleChange}
                  label="Tag Type"
                  variant="filled"
                >
                  {TAG_TYPES.map((type) => (
                    <MenuItem value={type.value} key={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ErrorMessage name="tagType" component={StyledFormicError} />
            </Box>

            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="tagName"
                label="Tag Name"
                type="text"
                id="tagName"
                value={formik?.values?.tagName}
              />
              <ErrorMessage name="tagName" component={StyledFormicError} />
            </Box>

            <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box mr={2}>
                <Button
                  variant="outlined"
                  onClick={handleCreateOrEditTagModalDismiss}
                >
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Grid>
          </Box>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default CreateOrEditTagModal;
